import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { i18n } from 'next-i18next';
import { useAuth } from '../auth';

export type ProfileType = 'Student' | 'Tutor' | string | null | undefined;
export type ExpectedRole = 'Student' | 'Tutor';

export interface RedirectResult {
  shouldRedirect: boolean;
  redirectPath: string | null;
  locale?: string;
}

export interface RoleRedirectState {
  isAuthorized: boolean;
  isLoading: boolean;
  isRedirecting: boolean;
}

/**
 * Checks if a user has a specific role.
 * Supports both single role (string) and multiple roles (array) profile_type.
 */
export function hasRole(
  profileType: ProfileType | string[],
  role: ExpectedRole,
): boolean {
  if (Array.isArray(profileType)) {
    return profileType.includes(role);
  }
  return profileType === role;
}

/**
 * Pure function that determines the redirect path based on the user's profile type
 * and the expected role for the current page.
 *
 * This function is extracted for easy unit testing.
 *
 * Key behaviors:
 * - Users with the expected role are allowed to stay on the page
 * - Users with both Student and Tutor roles (admins) are allowed on both /s/** and /t/** pages
 * - Tutors-only are redirected away from /s/** pages to /t/dashboard
 * - Students-only are redirected away from /t/** pages to /s/dashboard
 * - Unknown roles are redirected to home /
 */
export function getRedirectForRole(
  profileType: ProfileType | string[],
  expectedRole: ExpectedRole,
  userLocale?: string,
): RedirectResult {
  // Check if user has the expected role (supports both single role and array of roles)
  if (hasRole(profileType, expectedRole)) {
    return { shouldRedirect: false, redirectPath: null };
  }

  // User doesn't have the expected role, determine where to redirect
  const isStudent = hasRole(profileType, 'Student');
  const isTutor = hasRole(profileType, 'Tutor');

  if (expectedRole === 'Student') {
    // User is on a Student page but is not a Student
    if (isTutor) {
      return { shouldRedirect: true, redirectPath: '/t/dashboard' };
    }
    // Unknown profile type, redirect to home
    return { shouldRedirect: true, redirectPath: '/' };
  }

  if (expectedRole === 'Tutor') {
    // User is on a Tutor page but is not a Tutor
    if (isStudent) {
      return {
        shouldRedirect: true,
        redirectPath: '/s/dashboard',
        locale: userLocale,
      };
    }
    // Unknown profile type, redirect to home
    return { shouldRedirect: true, redirectPath: '/' };
  }

  // Fallback: no redirect
  return { shouldRedirect: false, redirectPath: null };
}

/**
 * Pure function that computes the authorization state for a given user and role.
 * Extracted for unit testing. Supports both string and array profile_type.
 */
export function computeRoleAuthState(
  currentUser: { profile_type: ProfileType | string[] } | null | undefined,
  expectedRole: ExpectedRole,
  loading: boolean,
  isRedirecting: boolean,
  localStorageAvailable: boolean,
): RoleRedirectState {
  const hasExpectedRole = currentUser
    ? hasRole(currentUser.profile_type, expectedRole)
    : false;
  const isLoading = loading || !localStorageAvailable || !hasExpectedRole;
  const isAuthorized = !loading && hasExpectedRole && !isRedirecting;

  return {
    isAuthorized,
    isLoading: isLoading && !isRedirecting,
    isRedirecting,
  };
}

/**
 * Custom hook that handles role-based redirects.
 * Ensures only one redirect is triggered to prevent infinite loops.
 */
export function useRoleRedirect(expectedRole: ExpectedRole): RoleRedirectState {
  const router = useRouter();
  const { currentUser, loading } = useAuth();
  const hasRedirected = useRef(false);

  // Check for auth token
  const authToken =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('authToken')
      : null;

  // Handle unauthenticated users
  useEffect(() => {
    if (hasRedirected.current) return;

    if (!authToken) {
      hasRedirected.current = true;
      router.push('/login');
      return;
    }

    if (authToken && !loading && !currentUser) {
      hasRedirected.current = true;
      const redirectPath = expectedRole === 'Student' ? '/login' : '/signup';
      router.push(redirectPath);
    }
  }, [authToken, loading, currentUser, router, expectedRole]);

  // Handle role-based redirects
  useEffect(() => {
    if (hasRedirected.current) return;
    if (loading || !currentUser) return;

    const result = getRedirectForRole(
      currentUser.profile_type,
      expectedRole,
      currentUser.locale?.i18n_form,
    );

    if (result.shouldRedirect && result.redirectPath) {
      hasRedirected.current = true;

      if (result.locale) {
        i18n?.changeLanguage(result.locale);
        router.push(result.redirectPath, result.redirectPath, {
          locale: result.locale,
        });
      } else {
        router.push(result.redirectPath);
      }
    }
  }, [loading, currentUser, expectedRole, router]);

  // Determine current state using hasRole() to properly handle array profile_type
  return computeRoleAuthState(
    currentUser,
    expectedRole,
    loading,
    hasRedirected.current,
    typeof localStorage !== 'undefined',
  );
}
